/**
 * Phone Number Groups Page
 *
 * Manage phone number groups with full CRUD operations.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Logger, LogCategory } from '@/utils/logger';
import { cn } from '@/utils';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { usePhoneNumberGroupStore, usePhoneNumberGroups, usePhoneNumberGroupsLoading } from '@/stores/phoneNumberGroupStore';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/lib/supabase';
import { PHONE_NUMBER_GROUP_COLORS, ROUTES, REPUTATION_SOURCES, REPUTATION_SOURCE_INFO, REPUTATION_SOURCE_COLORS } from '@/constants';
import type { PhoneNumberGroup, ReputationSource } from '@/types';
import {
  Plus,
  FolderOpen,
  Pencil,
  Trash2,
  Phone,
  Search,
  MoreVertical,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Calendar,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface GroupFormData {
  name: string;
  description: string;
  color: string;
  enabledSources: Record<ReputationSource, boolean>;
}

// ============================================================================
// Schedule Types & Constants
// ============================================================================

interface ScheduleRow {
  company_id: string;
  phone_number_group_id: string;
  service_name: string;
  service_type: string;
  cron_expression: string;
  is_enabled: boolean;
}

interface ServiceDef {
  name: string;
  displayName: string;
  type: 'live_call' | 'api';
}

const SCHEDULE_PRESETS = [
  { value: '0 6 * * *', label: 'Daily' },
  { value: '0 6 * * 1', label: 'Weekly' },
];

const DEFAULT_CRON = '0 6 * * *';

function scheduleKey(groupId: string, serviceName: string): string {
  return `${groupId}::${serviceName}`;
}

// ============================================================================
// Schedule Toggle Switch
// ============================================================================

interface ScheduleToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}

function ScheduleToggleSwitch({ enabled, onChange, disabled }: ScheduleToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`
        relative inline-flex items-center h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        ${enabled ? 'bg-primary-600' : 'bg-slate-200'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
          transition duration-200 ease-in-out
          ${enabled ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

// ============================================================================
// Schedule Section Component (inline per group)
// ============================================================================

interface ScheduleSectionProps {
  groupId: string;
  companyId: string;
  schedules: Map<string, ScheduleRow>;
  setSchedules: React.Dispatch<React.SetStateAction<Map<string, ScheduleRow>>>;
  showSuccess: (title: string, message: string) => void;
  showError: (title: string, message: string) => void;
}

function ScheduleSection({ groupId, companyId, schedules, setSchedules, showSuccess, showError }: ScheduleSectionProps) {
  const [saving, setSaving] = useState<string | null>(null);
  const [services, setServices] = useState<ServiceDef[]>([]);
  const [servicesLoaded, setServicesLoaded] = useState(false);

  // Fetch available test phones and API sources to build the service list dynamically
  useEffect(() => {
    async function loadServices() {
      const svcs: ServiceDef[] = [];

      // Fetch active test phones (each phone = a device-carrier combo)
      const { data: phones } = await (supabase.from('pnt_test_phones') as any)
        .select('id, name, carrier, os')
        .eq('is_active', true);

      for (const phone of (phones || [])) {
        const carrier = phone.carrier || 'Unknown';
        const os = phone.os === 'ios' ? 'iPhone' : 'Android';
        svcs.push({
          name: `${carrier.toLowerCase().replace(/\s+/g, '-')}-${phone.os}-${phone.id.slice(0, 8)}`,
          displayName: `${carrier} (${os})`,
          type: 'live_call',
        });
      }

      // Fetch enabled API sources
      const { data: sources } = await (supabase.from('pnt_api_sources') as any)
        .select('id, name, display_name, is_enabled')
        .eq('is_enabled', true);

      for (const source of (sources || [])) {
        svcs.push({
          name: source.name,
          displayName: source.display_name,
          type: 'api',
        });
      }

      setServices(svcs);
      setServicesLoaded(true);
    }
    loadServices();
  }, []);

  const getSchedule = (serviceName: string): ScheduleRow | undefined => {
    return schedules.get(scheduleKey(groupId, serviceName));
  };

  const upsertSchedule = async (
    serviceName: string,
    serviceType: string,
    displayName: string,
    patch: Partial<Pick<ScheduleRow, 'is_enabled' | 'cron_expression'>>,
  ) => {
    const key = scheduleKey(groupId, serviceName);
    const existing = schedules.get(key);
    setSaving(serviceName);

    const row: ScheduleRow = {
      company_id: companyId,
      phone_number_group_id: groupId,
      service_name: serviceName,
      service_type: serviceType,
      cron_expression: existing?.cron_expression ?? DEFAULT_CRON,
      is_enabled: existing?.is_enabled ?? false,
      ...patch,
    };

    setSchedules((prev) => {
      const next = new Map(prev);
      next.set(key, row);
      return next;
    });

    try {
      const { error } = await (supabase.from('pnt_schedules') as any)
        .upsert(row, { onConflict: 'company_id,phone_number_group_id,service_name' });
      if (error) throw error;
      showSuccess('Schedule saved', `Updated schedule for ${displayName}.`);
    } catch (err) {
      Logger.error(LogCategory.DATA, 'Failed to save schedule', 'ScheduleSection', undefined, err);
      showError('Save failed', 'Could not save schedule. Please try again.');
      setSchedules((prev) => {
        const next = new Map(prev);
        if (existing) next.set(key, existing);
        else next.delete(key);
        return next;
      });
    } finally {
      setSaving(null);
    }
  };

  const liveCallServices = services.filter((s) => s.type === 'live_call');
  const apiServices = services.filter((s) => s.type === 'api');

  const renderServiceRow = (service: ServiceDef) => {
    const sched = getSchedule(service.name);
    const enabled = sched?.is_enabled ?? false;
    const cron = sched?.cron_expression ?? DEFAULT_CRON;

    return (
      <div
        key={service.name}
        className="flex items-center justify-between gap-4 p-3 border border-slate-200 rounded-lg"
      >
        <div className="flex items-center gap-3 min-w-0">
          <ScheduleToggleSwitch
            enabled={enabled}
            onChange={(val) => upsertSchedule(service.name, service.type, service.displayName, { is_enabled: val })}
            disabled={saving !== null}
          />
          <span className="text-sm font-medium text-slate-900 truncate">
            {service.displayName}
          </span>
          <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
            {service.type === 'live_call' ? 'Device' : 'API'}
          </span>
        </div>
        <div className="w-36 shrink-0">
          <Select
            size="sm"
            value={cron}
            options={SCHEDULE_PRESETS}
            onChange={(e) => upsertSchedule(service.name, service.type, service.displayName, { cron_expression: e.target.value })}
            disabled={saving !== null}
          />
        </div>
      </div>
    );
  };

  if (!servicesLoaded) {
    return <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-400">Loading services...</div>;
  }

  if (services.length === 0) {
    return (
      <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-500">
        No test devices or API sources configured. Add test phones in the admin dashboard.
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
      {liveCallServices.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
            Device Testing
          </h4>
          <div className="space-y-2">
            {liveCallServices.map(renderServiceRow)}
          </div>
        </div>
      )}
      {apiServices.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
            API Checks
          </h4>
          <div className="space-y-2">
            {apiServices.map(renderServiceRow)}
          </div>
        </div>
      )}
    </div>
  );
}

const initialFormData: GroupFormData = {
  name: '',
  description: '',
  color: PHONE_NUMBER_GROUP_COLORS[0].value,
  enabledSources: {
    Nomorobo: true,
    RoboKiller: true,
    IceHook: true,
    TrueSpam: true,
    YouMail: true,
    Sensor: true,
    Verizon: true,
    SmartCall: false,
    TMobile: false,
    FTC: true,
  },
};

// ============================================================================
// Group Card Component
// ============================================================================

interface GroupCardProps {
  group: PhoneNumberGroup;
  onEdit: (group: PhoneNumberGroup) => void;
  onDelete: (group: PhoneNumberGroup) => void;
  companyId: string;
  schedules: Map<string, ScheduleRow>;
  setSchedules: React.Dispatch<React.SetStateAction<Map<string, ScheduleRow>>>;
  showSuccess: (title: string, message: string) => void;
  showError: (title: string, message: string) => void;
}

function GroupCard({ group, onEdit, onDelete, companyId, schedules, setSchedules, showSuccess, showError }: GroupCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${group.color || '#3B82F6'}20` }}
          >
            <FolderOpen
              className="h-5 w-5"
              style={{ color: group.color || '#3B82F6' }}
            />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{group.name}</h3>
            {group.description && (
              <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">
                {group.description}
              </p>
            )}
          </div>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <MoreVertical className="h-4 w-4" />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 top-8 w-36 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                <button
                  onClick={() => {
                    onEdit(group);
                    setShowMenu(false);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onDelete(group);
                    setShowMenu(false);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-danger-600 hover:bg-danger-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-slate-500">
            <Phone className="h-4 w-4" />
            <span className="text-sm font-medium">
              {group.phoneCount} {group.phoneCount === 1 ? 'number' : 'numbers'}
            </span>
          </div>

          <Link
            to={`${ROUTES.PHONE_NUMBERS}?group=${group.id}`}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            View numbers
          </Link>
        </div>

        <div className="pt-3 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-500 mb-2">Lookup Sources</p>
          <div className="flex items-center gap-1 text-xs text-slate-600">
            <span className="font-semibold">{group.enabledReputationSources.length}</span>
            <span>of {REPUTATION_SOURCES.length} enabled</span>
          </div>
        </div>

        {/* Collapsible Test Schedule Section */}
        <div className="pt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={() => setShowSchedule(!showSchedule)}
            className="flex items-center gap-2 w-full text-left text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            {showSchedule ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <Calendar className="h-4 w-4" />
            Test Schedule
          </button>
          {showSchedule && (
            <ScheduleSection
              groupId={group.id}
              companyId={companyId}
              schedules={schedules}
              setSchedules={setSchedules}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Toggle Switch Component
// ============================================================================

interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

function ToggleSwitch({ enabled, onChange }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      className={`
        relative inline-flex items-center h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        ${enabled ? 'bg-primary-600' : 'bg-slate-200'}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
          transition duration-200 ease-in-out
          ${enabled ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

// ============================================================================
// Color Picker Component
// ============================================================================

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
}

function ColorPicker({ value, onChange }: ColorPickerProps) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">Color</label>
      <div className="flex flex-wrap gap-2">
        {PHONE_NUMBER_GROUP_COLORS.map((color) => (
          <button
            key={color.id}
            type="button"
            onClick={() => onChange(color.value)}
            className={cn(
              'w-8 h-8 rounded-full transition-all',
              value === color.value
                ? 'ring-2 ring-offset-2 ring-primary-500 scale-110'
                : 'hover:scale-105'
            )}
            style={{ backgroundColor: color.value }}
            title={color.name}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Add/Edit Group Modal
// ============================================================================

interface GroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  group?: PhoneNumberGroup | null;
  onSave: (data: GroupFormData) => Promise<void>;
  isSaving: boolean;
}

function GroupModal({ isOpen, onClose, group, onSave, isSaving }: GroupModalProps) {
  const [formData, setFormData] = useState<GroupFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof GroupFormData, string>>>({});

  useEffect(() => {
    if (group) {
      const sources: Record<ReputationSource, boolean> = {} as Record<ReputationSource, boolean>;
      REPUTATION_SOURCES.forEach((source) => {
        sources[source] = group.enabledReputationSources.includes(source);
      });

      setFormData({
        name: group.name,
        description: group.description || '',
        color: group.color || PHONE_NUMBER_GROUP_COLORS[0].value,
        enabledSources: sources,
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
  }, [group, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof GroupFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Group name must be 50 characters or less';
    }

    if (formData.description.length > 200) {
      newErrors.description = 'Description must be 200 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await onSave(formData);
      onClose();
    } catch {
      // Error is handled by parent
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={group ? 'Edit Group' : 'Create Group'}
      size="lg"
    >
      <div className="space-y-4">
        <Input
          label="Group Name"
          placeholder="e.g., Sales Team"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          error={errors.name}
          required
        />

        <Input
          label="Description"
          placeholder="Optional description for this group"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          error={errors.description}
          helperText={`${formData.description.length}/200 characters`}
        />

        <ColorPicker
          value={formData.color}
          onChange={(color) => setFormData({ ...formData, color })}
        />

        <div className="space-y-3">
          <div className="border-t border-slate-200 pt-4">
            <label className="block text-sm font-medium text-slate-700 mb-3">
              Lookup Settings
            </label>
            <p className="text-sm text-slate-500 mb-4">
              Choose which reputation sources to query for phone numbers in this group
            </p>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {REPUTATION_SOURCES.map((source) => {
              const info = REPUTATION_SOURCE_INFO[source];
              const color = REPUTATION_SOURCE_COLORS[source];
              const isEnabled = formData.enabledSources[source];

              return (
                <div
                  key={source}
                  className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${color}20` }}
                    >
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{info.name}</p>
                      <p className="text-xs text-slate-500">{info.description}</p>
                    </div>
                  </div>
                  <ToggleSwitch
                    enabled={isEnabled}
                    onChange={() => setFormData({
                      ...formData,
                      enabledSources: {
                        ...formData.enabledSources,
                        [source]: !isEnabled,
                      },
                    })}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={isSaving}>
            {group ? 'Save Changes' : 'Create Group'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================================
// Delete Confirmation Modal
// ============================================================================

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: PhoneNumberGroup | null;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

function DeleteModal({ isOpen, onClose, group, onConfirm, isDeleting }: DeleteModalProps) {
  if (!group) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Group" size="sm">
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 bg-danger-50 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-danger-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-danger-800">
              Are you sure you want to delete "{group.name}"?
            </p>
            <p className="text-sm text-danger-700 mt-1">
              {group.phoneCount > 0
                ? `This group contains ${group.phoneCount} phone number${group.phoneCount !== 1 ? 's' : ''}. They will be unassigned from this group.`
                : 'This action cannot be undone.'}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} isLoading={isDeleting}>
            Delete Group
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================================
// Main Page Component
// ============================================================================

export function PhoneNumberGroupsPage() {
  const { success: showSuccess, error: showError } = useToast();
  const company = useAuthStore((state) => state.company);

  const { fetchGroups, createGroup, updateGroup, deleteGroup, ensureDefaultGroup } = usePhoneNumberGroupStore();
  const groups = usePhoneNumberGroups();
  const isLoading = usePhoneNumberGroupsLoading();

  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PhoneNumberGroup | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<PhoneNumberGroup | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [schedules, setSchedules] = useState<Map<string, ScheduleRow>>(new Map());

  useEffect(() => {
    Logger.info(LogCategory.UI, 'PhoneNumberGroupsPage mounted', 'PhoneNumberGroupsPage');
    const initializeGroups = async () => {
      await fetchGroups();
      if (company) {
        await ensureDefaultGroup(company.id);
      }
    };
    initializeGroups();
  }, [fetchGroups, ensureDefaultGroup, company]);

  // Fetch schedules for the company
  useEffect(() => {
    if (!company?.id) return;

    const fetchSchedules = async () => {
      try {
        const { data, error } = await (supabase.from('pnt_schedules') as any)
          .select('*')
          .eq('company_id', company.id);

        if (error) throw error;

        const map = new Map<string, ScheduleRow>();
        for (const row of data ?? []) {
          map.set(scheduleKey(row.phone_number_group_id, row.service_name), row);
        }
        setSchedules(map);
      } catch (err) {
        Logger.error(LogCategory.DATA, 'Failed to fetch schedules', 'PhoneNumberGroupsPage', undefined, err);
      }
    };

    fetchSchedules();
  }, [company?.id]);

  const filteredGroups = groups.filter((group) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      group.name.toLowerCase().includes(query) ||
      group.description?.toLowerCase().includes(query)
    );
  });

  const handleCreateGroup = async (data: GroupFormData) => {
    if (!company) return;

    setIsSaving(true);
    try {
      const enabledSources = REPUTATION_SOURCES.filter((source) => data.enabledSources[source]);

      await createGroup({
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        color: data.color,
        companyId: company.id,
        enabledReputationSources: enabledSources,
      });
      showSuccess('Group created', `"${data.name}" has been created successfully.`);
      setIsCreateModalOpen(false);
    } catch (error) {
      Logger.error(LogCategory.DATA, 'Failed to create group', 'handleCreateGroup', undefined, error);
      showError('Error', error instanceof Error ? error.message : 'Failed to create group');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateGroup = async (data: GroupFormData) => {
    if (!editingGroup) return;

    setIsSaving(true);
    try {
      const enabledSources = REPUTATION_SOURCES.filter((source) => data.enabledSources[source]);

      await updateGroup(editingGroup.id, {
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        color: data.color,
        enabledReputationSources: enabledSources,
      });
      showSuccess('Group updated', `"${data.name}" has been updated successfully.`);
      setEditingGroup(null);
    } catch (error) {
      Logger.error(LogCategory.DATA, 'Failed to update group', 'handleUpdateGroup', undefined, error);
      showError('Error', error instanceof Error ? error.message : 'Failed to update group');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;

    setIsSaving(true);
    try {
      await deleteGroup(deletingGroup.id);
      showSuccess('Group deleted', `"${deletingGroup.name}" has been deleted.`);
      setDeletingGroup(null);
    } catch (error) {
      Logger.error(LogCategory.DATA, 'Failed to delete group', 'handleDeleteGroup', undefined, error);
      showError('Error', error instanceof Error ? error.message : 'Failed to delete group');
    } finally {
      setIsSaving(false);
    }
  };

  const totalPhoneCount = groups.reduce((sum, g) => sum + g.phoneCount, 0);

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Phone Number Groups</h1>
            <p className="text-slate-500 mt-1">
              Organize your phone numbers into groups for easier management
            </p>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Create Group
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <FolderOpen className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{groups.length}</p>
                  <p className="text-sm text-slate-500">Total Groups</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-success-100 rounded-lg">
                  <Phone className="h-5 w-5 text-success-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{totalPhoneCount}</p>
                  <p className="text-sm text-slate-500">Assigned Numbers</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-warning-100 rounded-lg">
                  <Phone className="h-5 w-5 text-warning-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {groups.length > 0
                      ? Math.round(totalPhoneCount / groups.length)
                      : 0}
                  </p>
                  <p className="text-sm text-slate-500">Avg per Group</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Groups Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : filteredGroups.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredGroups.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                onEdit={setEditingGroup}
                onDelete={setDeletingGroup}
                companyId={company?.id ?? ''}
                schedules={schedules}
                setSchedules={setSchedules}
                showSuccess={showSuccess}
                showError={showError}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <div className="mx-auto w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <FolderOpen className="h-6 w-6 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-1">
                  {searchQuery ? 'No groups found' : 'No groups yet'}
                </h3>
                <p className="text-slate-500 mb-4">
                  {searchQuery
                    ? 'Try adjusting your search query'
                    : 'Create your first group to organize your phone numbers'}
                </p>
                {!searchQuery && (
                  <Button
                    onClick={() => setIsCreateModalOpen(true)}
                    leftIcon={<Plus className="h-4 w-4" />}
                  >
                    Create Group
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Modal */}
      <GroupModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateGroup}
        isSaving={isSaving}
      />

      {/* Edit Modal */}
      <GroupModal
        isOpen={!!editingGroup}
        onClose={() => setEditingGroup(null)}
        group={editingGroup}
        onSave={handleUpdateGroup}
        isSaving={isSaving}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={!!deletingGroup}
        onClose={() => setDeletingGroup(null)}
        group={deletingGroup}
        onConfirm={handleDeleteGroup}
        isDeleting={isSaving}
      />
    </AppLayout>
  );
}
